import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";

export type Product = types.Product;

// Uses the current route to determine what the 'product' should be and fetches
// the product description for you.
export const useProductStore = defineEntityStore("product", () => {
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();

    const product = ref<Product | undefined>(undefined);
    const router = useRouter();
    const syncStore = useSyncStore();
    const entityId = computed(() => product.value?.identity);
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PRODUCT,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.PRODUCT);

    const { product: productInfo } = useCurrentEntityManager();

    const isFetching = ref(false);
    const isFetchingDescription = ref(false);

    const controller = ref(new AbortController());
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(product);

    async function fetchProduct(force = false) {
        const productId = productInfo.value?.identity;

        // In case the same product is selected, return early.
        if (!force && product.value && product.value.identity === productId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!productId) {
            description.value = undefined;
            product.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        product.value = undefined;

        try {
            const { data, response } =
                await api.startProductProductMgtGetProductStartPost({
                    body: {
                        product_id: `${productId}`,
                    },
                    signal: controller.value.signal,
                });

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "product",
                            id: productId,
                        },
                    });
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            product.value = data;
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
        // Fetch description while we're still in the try block before setting isFetching=false
        if (product.value) {
            await fetchDescription(productId);
        }
    }
    watch(
        () => productInfo.value?.identity,
        () => fetchProduct(),
        { immediate: true }
    );

    const fetchDescription = async (currentProductId?: string | undefined) => {
        if (!description.value && product.value) {
            // Store the current product ID to detect changes during async operation
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    product.value,
                    "productDescription",
                    controller.value.signal
                );

                // Check if product has changed during the async operation
                if (
                    !product.value ||
                    product.value.identity !== currentProductId
                ) {
                    console.debug(
                        "Product changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingDescription.value = false;
            }
        }
    };

    const isModified = computed(() => {
        if (isFetching.value) {
            return false;
        }
        return Boolean(isNameModified.value || description.value?.isModified);
    });

    const queueSave = async () => {
        if (!isModified.value || !product.value) {
            return;
        }

        const updateContent: types.StartProductProductMgtUpdateProductStartPostData =
            {
                body: {
                    product_id: product.value.identity,
                },
                url: "/product/product_mgt/update_product/start",
            };

        if (isNameModified.value) {
            updateContent.body.name = product.value.name;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            updateContent.body.product_id &&
            Object.keys(updateContent.body).length === 1
        ) {
            // Only product_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.PRODUCT,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };
    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.PRODUCT &&
                identity.entityId === product.value?.identity
            ) {
                fetchProduct(true);
            }
        }
    );

    return {
        product,
        description,
        isFetching: computed(
            () => isFetching.value || isFetchingDescription.value
        ),
        isModified,
        reset,
    };
});

export const useProductManager = () => storeToRefs(useProductStore());
